/**
 * Wall clocks in IANA timezones, Intl only (browser and Node) — moved from
 * the backend's recipient-window util (15/10/2026) so the editor converts
 * Danish times exactly like the planner. Handles DST edges (a skipped
 * spring-forward time → the next valid instant; a repeated fall-back time →
 * the later one) and fractional offsets (+9:30 Adelaide, +5:45 Kathmandu).
 * For Media Devoted | Created By Ciro Autiero
 */

const MINUTE_MS = 60_000;

const partsFormatters = new Map<string, Intl.DateTimeFormat>();

function getPartsFormatter(tz: string): Intl.DateTimeFormat {
  let formatter = partsFormatters.get(tz);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    partsFormatters.set(tz, formatter);
  }
  return formatter;
}

/** Wall-clock reading of `instant` in `tz`, re-encoded as UTC epoch ms. */
function wallAsUtcMs(instant: Date, tz: string): number {
  const p: Record<string, number> = {};
  for (const { type, value } of getPartsFormatter(tz).formatToParts(instant)) {
    if (type !== "literal") p[type] = Number(value);
  }
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/** UTC offset of `tz` at the given epoch ms (positive = ahead of UTC). */
function offsetMs(instantMs: number, tz: string): number {
  return wallAsUtcMs(new Date(instantMs), tz) - instantMs;
}

/** UTC offset of `tz` at `at`, in minutes (positive = ahead of UTC). */
export function tzOffsetMin(tz: string, at: Date): number {
  return Math.round(offsetMs(at.getTime(), tz) / MINUTE_MS);
}

/** First valid instant after a spring-forward gap that skipped the wall time. */
function nextValidInstant(desiredWallMs: number, tz: string): Date {
  const offsetA = offsetMs(desiredWallMs, tz);
  const offsetB = offsetMs(desiredWallMs - offsetA, tz);
  const offsetBefore = Math.min(offsetA, offsetB);
  let lo = desiredWallMs - Math.max(offsetA, offsetB);
  let hi = desiredWallMs - offsetBefore;
  while (hi - lo > MINUTE_MS) {
    const mid = lo + Math.floor((hi - lo) / (2 * MINUTE_MS)) * MINUTE_MS;
    if (offsetMs(mid, tz) === offsetBefore) lo = mid;
    else hi = mid;
  }
  return new Date(hi);
}

/** "YYYY-MM-DD" of `now` as seen on the wall clock in the IANA `tz`. */
export function localDateInTz(now: Date, tz: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** Minutes past local midnight of `instant` on the wall clock in `tz`. */
export function wallMinutesInTz(instant: Date, tz: string): number {
  const ms = wallAsUtcMs(instant, tz);
  return Math.floor((((ms / MINUTE_MS) % 1440) + 1440) % 1440);
}

export interface WallTimeToUtcParams {
  dateIso: string;
  minutesFromMidnight: number;
  tz: string;
}

/**
 * UTC instant of the wall time `minutesFromMidnight` past local midnight on
 * `dateIso` in `tz`, via offset probing (two iterations cover DST).
 */
export function wallTimeToUtc(params: WallTimeToUtcParams): Date {
  const { dateIso, minutesFromMidnight, tz } = params;
  const [year, month, day] = dateIso.split("-").map(Number);
  const desired = Date.UTC(year, month - 1, day) + minutesFromMidnight * MINUTE_MS;
  let candidate = desired - offsetMs(desired, tz);
  candidate = desired - offsetMs(candidate, tz);
  if (wallAsUtcMs(new Date(candidate), tz) === desired) return new Date(candidate);
  return nextValidInstant(desired, tz);
}

/** "YYYY-MM-DD" + n calendar days. */
export function addDaysIso(dateIso: string, days: number): string {
  const base = new Date(`${dateIso}T00:00:00Z`);
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
}

/** Whole calendar days from `a` to `b` ("YYYY-MM-DD"). */
export function daysBetweenIso(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}
